import { DashboardModal } from "@dashboard/components/Modal";
import {
  ProductAttributeType,
  useAssignProductAttributeMutation,
  useProductTypeDetailsQuery,
  useUnassignProductAttributeMutation,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { buttonMessages, commonMessages } from "@dashboard/intl";
import { Box, Button, Option, Select, Text } from "@saleor/macaw-ui-next";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

type Scope = "product" | "variant" | "both";

interface AttributeRow {
  id: string;
  name: string;
  /** Scope as currently persisted on the product type. */
  initialScope: Scope;
  /** Scope selected in the dialog (or null when marked for removal). */
  scope: Scope | null;
}

interface ManageAttributeScopeModalProps {
  open: boolean;
  onClose: () => void;
  productTypeId: string;
  productTypeName: string;
}

const scopeOptions: Array<Option & { value: Scope }> = [
  { value: "product", label: "Product" },
  { value: "variant", label: "Variant" },
  { value: "both", label: "Both" },
];

const scopeTypes = (scope: Scope): ProductAttributeType[] => {
  if (scope === "product") {
    return [ProductAttributeType.PRODUCT];
  }

  if (scope === "variant") {
    return [ProductAttributeType.VARIANT];
  }

  return [ProductAttributeType.PRODUCT, ProductAttributeType.VARIANT];
};

export const ManageAttributeScopeModal = ({
  open,
  onClose,
  productTypeId,
  productTypeName,
}: ManageAttributeScopeModalProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [rows, setRows] = useState<AttributeRow[]>([]);
  const [saving, setSaving] = useState(false);

  const { data, loading, refetch } = useProductTypeDetailsQuery({
    variables: { id: productTypeId },
    skip: !open,
    fetchPolicy: "network-only",
  });

  const initialRows = useMemo<AttributeRow[]>(() => {
    const productType = data?.productType;

    if (!productType) {
      return [];
    }

    const scopeById = new Map<string, { name: string; product: boolean; variant: boolean }>();

    for (const attr of productType.productAttributes ?? []) {
      scopeById.set(attr.id, {
        name: attr.name ?? attr.slug ?? attr.id,
        product: true,
        variant: false,
      });
    }
    for (const attr of productType.variantAttributes ?? []) {
      const existing = scopeById.get(attr.id);

      if (existing) {
        existing.variant = true;
      } else {
        scopeById.set(attr.id, {
          name: attr.name ?? attr.slug ?? attr.id,
          product: false,
          variant: true,
        });
      }
    }

    return [...scopeById.entries()]
      .map(([id, info]) => {
        const scope: Scope =
          info.product && info.variant ? "both" : info.product ? "product" : "variant";

        return { id, name: info.name, initialScope: scope, scope };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const [assignAttribute] = useAssignProductAttributeMutation({});
  const [unassignAttribute] = useUnassignProductAttributeMutation({});

  const handleScopeChange = (id: string, scope: Scope) => {
    setRows(current => current.map(row => (row.id === id ? { ...row, scope } : row)));
  };

  const handleRemove = (id: string) => {
    setRows(current => current.map(row => (row.id === id ? { ...row, scope: null } : row)));
  };

  const hasChanges = rows.some(row => row.scope !== row.initialScope);

  const handleSave = async () => {
    setSaving(true);

    try {
      for (const row of rows) {
        if (row.scope === row.initialScope) {
          continue;
        }

        const currentTypes = scopeTypes(row.initialScope);
        const desiredTypes = row.scope ? scopeTypes(row.scope) : [];
        const removesAScope = currentTypes.some(type => !desiredTypes.includes(type));

        if (desiredTypes.length === 0) {
          // Removed entirely.
          await unassignAttribute({ variables: { id: productTypeId, ids: [row.id] } });
          continue;
        }

        if (removesAScope) {
          // Unassign clears both scopes (and their values), so re-assign the kept scope(s).
          await unassignAttribute({ variables: { id: productTypeId, ids: [row.id] } });
          await assignAttribute({
            variables: {
              id: productTypeId,
              operations: desiredTypes.map(type => ({ id: row.id, type })),
            },
          });
          continue;
        }

        // Only added a scope (e.g. variant -> both): assign just the new one.
        const addedTypes = desiredTypes.filter(type => !currentTypes.includes(type));

        await assignAttribute({
          variables: {
            id: productTypeId,
            operations: addedTypes.map(type => ({ id: row.id, type })),
          },
        });
      }

      await refetch();
      notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      onClose();
    } catch {
      notify({ status: "error", text: intl.formatMessage(commonMessages.somethingWentWrong) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Grid>
          <DashboardModal.Header>
            <Text size={5} fontWeight="bold">
              <FormattedMessage
                defaultMessage="Manage attributes — {name}"
                id="6mSMI6"
                description="manage product type attribute scope dialog title"
                values={{ name: productTypeName }}
              />
            </Text>
          </DashboardModal.Header>

          <Box
            backgroundColor="warning1"
            padding={3}
            borderRadius={3}
            data-test-id="manage-attributes-warning"
          >
            <Text size={2}>
              <FormattedMessage
                defaultMessage="Scope is a property of the product type, so changes here affect every product of type “{name}”. Removing a scope clears that scope's existing values on all of those products."
                id="WUfr+E"
                description="manage attribute scope warning"
                values={{ name: productTypeName }}
              />
            </Text>
          </Box>

          {loading && rows.length === 0 ? (
            <Text size={2}>
              <FormattedMessage {...commonMessages.loading} />
            </Text>
          ) : rows.length === 0 ? (
            <Text size={2}>
              <FormattedMessage
                defaultMessage="This product type has no attributes assigned."
                id="QzUtbW"
                description="empty manage attributes dialog"
              />
            </Text>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {rows.map(row => (
                <Box
                  key={row.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={3}
                  data-test-id={`manage-attribute-row-${row.id}`}
                >
                  <Text size={3} color={row.scope === null ? "default2" : undefined}>
                    {row.name}
                    {row.scope === null && (
                      <Text size={2} color="default2">
                        {" "}
                        <FormattedMessage
                          defaultMessage="(will be removed)"
                          id="5wrixT"
                          description="attribute marked for removal"
                        />
                      </Text>
                    )}
                  </Text>
                  <Box display="flex" alignItems="center" gap={2}>
                    {row.scope === null ? (
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => handleScopeChange(row.id, row.initialScope)}
                      >
                        <FormattedMessage {...buttonMessages.undo} />
                      </Button>
                    ) : (
                      <>
                        <Select
                          data-test-id={`scope-select-${row.id}`}
                          size="small"
                          options={scopeOptions}
                          value={scopeOptions.find(option => option.value === row.scope) ?? ""}
                          onChange={option =>
                            handleScopeChange(row.id, (option as Option).value as Scope)
                          }
                        />
                        <Button
                          variant="tertiary"
                          size="small"
                          onClick={() => handleRemove(row.id)}
                          data-test-id={`remove-attribute-${row.id}`}
                        >
                          <FormattedMessage {...buttonMessages.remove} />
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <DashboardModal.Actions>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              <FormattedMessage {...buttonMessages.cancel} />
            </Button>
            <Button onClick={handleSave} disabled={saving || !hasChanges}>
              <FormattedMessage {...buttonMessages.save} />
            </Button>
          </DashboardModal.Actions>
        </DashboardModal.Grid>
      </DashboardModal.Content>
    </DashboardModal>
  );
};
